// SUB-OFFICE 7 — THE MACHINE FLOOR (zone: machine_floor, Floor -1). Build
// increment M-D. The datacenter where the Registrar physically runs — the
// second-act thesis reveal: the Machine has no hands, so it uses yours. This is
// the flagship streamed zone; it retires buildDescentStub and drops into the
// exact ZoneBuild slot the stub held (spawn 'from_cab' off the cab, plus
// 'arrival'/'resume' for the deferred conscription stair).
//
// EVERYTHING INVERTS DOWN HERE. Light comes from BELOW (phosphor #43E86C,
// unrestricted, ambient — the medium, not an accent). Bone/manila are nearly
// absent (the one room not made of paper). Amber (#D98A21) is reserved for the
// Machine's VOICE only. The applyGrade that lights this floor swaps the
// hemisphere poles (bright phosphor GROUND, near-black SKY) so faces pointing
// DOWN catch green and faces pointing UP fall to near-black — that single
// physical fact IS the art direction. It is applied by the controller; this
// module builds the self-illuminated practicals that are the only real light:
// per-aisle underglow strips, coolant runs, LED rack constellations, the
// through-grate phosphor at the landing, ground haze, heat shimmer.
//
// Coordinate island OX=320/OZ=0 (world x[279,361]) — clear of floor7 and the
// old stub. Everything below is authored in a CENTERED LOCAL frame; MM() adds
// the island offset to every vertex at build time. Budgets (spec gates for the
// whole floor incl. deferred M-E): <=45 draw calls / <=25k tris. M-D lands ~18
// draws / ~20k tris (racks are the sink and the hard constraint).
//
// All player-facing voice is verbatim from content/machine-floor.json.

import {
  AdditiveBlending, BoxGeometry, BufferAttribute, BufferGeometry,
  CanvasTexture, Color, DoubleSide, DynamicDrawUsage, Group,
  InstancedMesh, Matrix4, Mesh, MeshBasicMaterial, MeshLambertMaterial,
  PlaneGeometry, type Material,
} from 'three';
import { PAL, css, shade } from '../../palette';
import { patchMaterial } from '../../render/scene';
import { Chyron, drawDotText, dotTextWidth, mkCanvas, toTexture } from '../../render/textures';
import type { CollisionBounds } from '../collision';
import type { ZoneManifest, ZoneSpawn, ZoneHotspot, WallSeg, SolidRect, ZoneRect } from './types';
import content from '../../../content/machine-floor.json';

// --- island offset (local -> world) -----------------------------------------
const OX = 320, OZ = 0;
const WALL_H = 5.0;

/** hex -> linear-space THREE.Color (materials expect linear). */
const colr = (hex: number): Color => new Color(hex).convertSRGBToLinear();

/** local matrix with the island offset baked in. */
const MM = (x: number, y: number, z: number, sx = 1, sy = 1, sz = 1): Matrix4 =>
  new Matrix4().makeScale(sx, sy, sz).setPosition(x + OX, y, z + OZ);

/** a single-int hash -> [0,1) (mulberry32-derived; seeded_variation law — every
 *  player sees the identical constellation, NEVER Math.random). */
function h1(n: number): number {
  let t = (n + 0x9e3779b9) | 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

// ============================================================================
// Batches — merge transformed geometries into one soup so a whole material is
// one draw. `Batch` for pos/nor/uv props; `CBatch` adds a flat per-box vertex
// color (racks are lambert-vertex-color so carbon body + brass pull + dark
// recess ride ONE InstancedMesh).
// ============================================================================

interface UvRegion { u0: number; v0: number; u1: number; v1: number }
const FULL_UV: UvRegion = { u0: 0, v0: 0, u1: 1, v1: 1 };

class Batch {
  private pos: number[] = []; private nor: number[] = []; private uv: number[] = [];
  add(geo: BufferGeometry, m: Matrix4, region: UvRegion = FULL_UV): void {
    const g = geo.index ? geo.toNonIndexed() : geo;
    g.applyMatrix4(m);
    const p = g.attributes.position.array as ArrayLike<number>;
    const n = g.attributes.normal ? g.attributes.normal.array as ArrayLike<number> : null;
    const u = g.attributes.uv ? g.attributes.uv.array as ArrayLike<number> : null;
    const count = g.attributes.position.count;
    for (let i = 0; i < count; i++) {
      this.pos.push(p[i * 3], p[i * 3 + 1], p[i * 3 + 2]);
      if (n) this.nor.push(n[i * 3], n[i * 3 + 1], n[i * 3 + 2]); else this.nor.push(0, 1, 0);
      const su = u ? u[i * 2] : 0, sv = u ? u[i * 2 + 1] : 0;
      this.uv.push(region.u0 + su * (region.u1 - region.u0), region.v0 + sv * (region.v1 - region.v0));
    }
    if (g !== geo) g.dispose();
    geo.dispose();
  }
  get empty(): boolean { return this.pos.length === 0; }
  get tris(): number { return this.pos.length / 9; }
  build(): BufferGeometry {
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(new Float32Array(this.pos), 3));
    g.setAttribute('normal', new BufferAttribute(new Float32Array(this.nor), 3));
    g.setAttribute('uv', new BufferAttribute(new Float32Array(this.uv), 2));
    return g;
  }
}

/** colored merge for the rack kit (position/normal/color; no uv needed). */
class CBatch {
  private pos: number[] = []; private nor: number[] = []; private col: number[] = [];
  add(geo: BufferGeometry, m: Matrix4, color: Color): void {
    const g = geo.index ? geo.toNonIndexed() : geo;
    g.applyMatrix4(m);
    const p = g.attributes.position.array as ArrayLike<number>;
    const n = g.attributes.normal.array as ArrayLike<number>;
    const count = g.attributes.position.count;
    for (let i = 0; i < count; i++) {
      this.pos.push(p[i * 3], p[i * 3 + 1], p[i * 3 + 2]);
      this.nor.push(n[i * 3], n[i * 3 + 1], n[i * 3 + 2]);
      this.col.push(color.r, color.g, color.b);
    }
    if (g !== geo) g.dispose();
    geo.dispose();
  }
  get tris(): number { return this.pos.length / 9; }
  build(): BufferGeometry {
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(new Float32Array(this.pos), 3));
    g.setAttribute('normal', new BufferAttribute(new Float32Array(this.nor), 3));
    g.setAttribute('color', new BufferAttribute(new Float32Array(this.col), 3));
    return g;
  }
}

// ============================================================================
// ONE rack, in a canonical orientation (wide along local X = 1.0m, deep along
// Z = 0.6m, 2.2m tall, base at y=0). <=70 tris. Carbon body + top chamfer lip +
// dark front recess + 3 brass pull boxes + inspection plate w/ 2 bolt dots.
// Vertex-colored so the whole rack kit is one InstancedMesh (one draw).
// ============================================================================
function makeRackGeo(): { geo: BufferGeometry; tris: number } {
  const b = new CBatch();
  const carbon = colr(shade(PAL.carbon, 0.06));
  const carbonDark = colr(shade(PAL.carbon, -0.4));
  const brass = colr(PAL.brass);
  // body
  b.add(new BoxGeometry(1.0, 2.2, 0.6), MMlocal(0, 1.1, 0), carbon);
  // top chamfer lip (proud 2-3cm)
  b.add(new BoxGeometry(1.06, 0.05, 0.66), MMlocal(0, 2.2, 0), colr(shade(PAL.carbon, 0.14)));
  // front-face recess (dark inset drawer seam) on +Z face
  b.add(new BoxGeometry(0.86, 1.9, 0.04), MMlocal(0, 1.05, 0.31), carbonDark);
  // three brass pulls stacked (read as a horizontal bar at distance)
  for (const yy of [0.55, 1.1, 1.65]) b.add(new BoxGeometry(0.5, 0.05, 0.05), MMlocal(0, yy, 0.335), brass);
  // inspection plate + 2 bolt dots
  b.add(new BoxGeometry(0.16, 0.16, 0.03), MMlocal(0.32, 1.9, 0.32), colr(shade(PAL.carbon, 0.2)));
  b.add(new BoxGeometry(0.03, 0.03, 0.02), MMlocal(0.27, 1.95, 0.335), brass);
  b.add(new BoxGeometry(0.03, 0.03, 0.02), MMlocal(0.37, 1.85, 0.335), brass);
  return { geo: b.build(), tris: b.tris };
}
// local (no island offset) matrix for geometry authored at the origin
const MMlocal = (x: number, y: number, z: number, sx = 1, sy = 1, sz = 1): Matrix4 =>
  new Matrix4().makeScale(sx, sy, sz).setPosition(x, y, z);

// ============================================================================
// LED constellation atlas — PAT seeded 8x24 pilot grids side by side in ONE
// CanvasTexture. Per-rack UV region picks a pattern (so Hall A != Hall B from
// one texture). Redraws at 10Hz: mostly steady, a few cells toggling, 2% amber
// at STABLE seeded positions (a kept defect stays put). Below each grid a
// dot-matrix row designation (the ONLY text — a row number, never a name).
// ============================================================================
const LED_PAT = 6;      // distinct seeded patterns
const LED_COLS = 8, LED_ROWS = 24;
const CELL = 5;         // px per cell
const GRID_W = LED_COLS * CELL, GRID_H = LED_ROWS * CELL;
const PAD = 3, LABEL_H = 12;
const PW = GRID_W + PAD * 2, PH = GRID_H + PAD * 2 + LABEL_H;

function makeLedAtlas(): {
  canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D; texture: CanvasTexture;
  redraw: (bucket: number, boil: boolean) => void; region: (pat: number) => UvRegion;
} {
  const [canvas, ctx] = mkCanvas(PW * LED_PAT, PH);
  const tex = toTexture(canvas, { nearest: true });
  const green = css(PAL.phosphor), greenDim = css(shade(PAL.phosphor, -0.62)), amber = css(PAL.amber);
  const redraw = (bucket: number, boil: boolean): void => {
    ctx.fillStyle = css(shade(PAL.carbon, -0.5));
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    for (let pat = 0; pat < LED_PAT; pat++) {
      const ox = pat * PW + PAD, oy = PAD;
      for (let cy = 0; cy < LED_ROWS; cy++) {
        for (let cx = 0; cx < LED_COLS; cx++) {
          const cell = cy * LED_COLS + cx;
          const stableAmber = h1(cell * 131 + pat * 977) < 0.02;
          let color: string;
          if (stableAmber) {
            color = amber; // a kept defect: same freckle every shift, forbidden to fix
          } else {
            // cascade + steady base + occasional per-cell toggle (computation, not signalling)
            const casc = ((cx + bucket) % LED_COLS) === 0 && h1(cell * 17 + pat) < 0.5;
            const jit = boil && cy < 3 && h1(cell + bucket * 7 + pat * 3) < 0.5; // top-row heat boil
            const base = h1(cell * 131 + pat * 977 + bucket * 31) > 0.42;
            color = base || casc || jit ? green : greenDim;
          }
          ctx.fillStyle = color;
          ctx.fillRect(ox + cx * CELL, oy + cy * CELL, CELL - 1, CELL - 1);
        }
      }
      // row designation (dot-matrix), the only text on the floor
      const desig = 'R' + String(pat * 7 + 3).padStart(2, '0') + '-' + String((pat * 13) % 90 + 10);
      drawDotText(ctx, desig, ox, oy + GRID_H + 2, greenDim, { dot: 1.4 });
    }
    tex.needsUpdate = true;
  };
  redraw(0, false);
  const region = (pat: number): UvRegion => ({
    u0: (pat * PW) / canvas.width, u1: (pat * PW + PW) / canvas.width, v0: 0, v1: 1,
  });
  return { canvas, ctx, texture: tex, redraw, region };
}

// ============================================================================
// Signage atlas — verbatim rack-hall signage on carbon plates (ALL CAPS,
// housed: the building still pretending to be an office).
// ============================================================================
function makeSignAtlas(lines: string[]): { texture: CanvasTexture; region: (i: number) => UvRegion } {
  const cols = 2, rows = Math.max(1, Math.ceil(lines.length / cols)), CW = 512, CH = 200;
  const [c, ctx] = mkCanvas(CW * cols, CH * rows);
  const wrap = (text: string, maxW: number, dot: number): string[] => {
    const out: string[] = []; let cur = '';
    for (const w of text.split(' ')) {
      const trial = cur ? cur + ' ' + w : w;
      if (dotTextWidth(trial, { dot }) > maxW && cur) { out.push(cur); cur = w; } else cur = trial;
    }
    if (cur) out.push(cur);
    return out;
  };
  for (let i = 0; i < lines.length; i++) {
    const col = i % cols, row = (i / cols) | 0, ox = col * CW, oy = row * CH;
    ctx.fillStyle = css(shade(PAL.carbon, 0.05)); ctx.fillRect(ox + 8, oy + 8, CW - 16, CH - 16);
    ctx.strokeStyle = 'rgba(67,232,108,0.20)'; ctx.lineWidth = 3;
    ctx.strokeRect(ox + 14, oy + 14, CW - 28, CH - 28);
    const dot = 4, wrapped = wrap(lines[i], CW - 70, dot), lineH = 7 * dot + 8;
    let ty = oy + CH / 2 - (wrapped.length - 1) * lineH / 2 - 12;
    for (const wln of wrapped) {
      const w = dotTextWidth(wln, { dot });
      drawDotText(ctx, wln, ox + (CW - w) / 2, ty, css(shade(PAL.phosphor, -0.15)), { dot });
      ty += lineH;
    }
  }
  const t = toTexture(c, { nearest: true });
  const region = (i: number): UvRegion => {
    const col = i % cols, row = (i / cols) | 0;
    return { u0: col / cols, u1: (col + 1) / cols, v0: 1 - (row + 1) / rows, v1: 1 - row / rows };
  };
  return { texture: t, region };
}

/** phosphor wayfinding decal (bright on dark, additive-read). */
function makeDecal(line: string): CanvasTexture {
  const [c, ctx] = mkCanvas(512, 128);
  const dot = 5, w = dotTextWidth(line, { dot }), scale = Math.min(1, (512 - 30) / Math.max(1, w));
  drawDotText(ctx, line, (512 - w * scale) / 2, 44, css(PAL.phosphor), { dot: dot * scale });
  return toTexture(c, { nearest: true });
}

// ============================================================================
export interface MachineFloorBuild {
  group: Group;
  manifest: ZoneManifest;
  hotspots: ZoneHotspot[];
  chyron: Chyron;
  /** 10Hz LED constellation redraw (computation, not signalling). */
  tickLED: (dt: number) => void;
  /** advance the coolant slugs + ease the diegetic verdict pulse. */
  tickCoolant: (dt: number) => void;
  /** heat-shimmer opacity LFO (still under reduced motion). */
  tickShimmer: (dt: number) => void;
  /** diegetic verdict: the aisle you worked RESPONDS (no Stamp Arm on -1). */
  respondVerdict: (band: 'APPROVED' | 'AWD' | 'TOLERATED' | 'DENIED') => void;
  tris: number;
  buildMs: number;
  dispose(): void;
}

export function buildMachineFloor(reducedMotion: boolean): MachineFloorBuild {
  const t0 = performance.now();
  const mf = content.machineFloor;
  const group = new Group();
  const disposables: { dispose(): void }[] = [];
  const track = <T extends Material | CanvasTexture>(x: T): T => { disposables.push(x); return x; };

  // --- materials -------------------------------------------------------------
  const structMat = track(new MeshLambertMaterial({ color: colr(shade(PAL.carbon, 0.02)), flatShading: true }));
  patchMaterial(structMat);
  const rackMat = track(new MeshLambertMaterial({ vertexColors: true, flatShading: true }));
  patchMaterial(rackMat);
  // emissive practicals — the ONLY light. fog:true so far ones dissolve into the
  // green-dark at the vanishing point (the dread); dimExempt keeps them lit.
  const underMat = track(new MeshBasicMaterial({ color: colr(PAL.phosphor) }));
  patchMaterial(underMat, { dimExempt: true });
  const glowMat = track(new MeshBasicMaterial({ color: colr(PAL.phosphor), transparent: true, opacity: 0.16, blending: AdditiveBlending, depthWrite: false, side: DoubleSide }));
  const coolantCore = track(new MeshBasicMaterial({ color: colr(PAL.phosphor), transparent: true, opacity: 0.5, blending: AdditiveBlending, depthWrite: false }));
  const coolantShell = track(new MeshBasicMaterial({ color: colr(shade(PAL.phosphor, -0.3)), transparent: true, opacity: 0.14, depthWrite: false, side: DoubleSide }));
  const slugMat = track(new MeshBasicMaterial({ color: colr(shade(PAL.phosphor, 0.25)), transparent: true, opacity: 0.85, blending: AdditiveBlending, depthWrite: false }));
  const hazeMat = track(new MeshBasicMaterial({ color: colr(PAL.phosphor), transparent: true, opacity: 0.04, blending: AdditiveBlending, depthWrite: false, fog: false, side: DoubleSide }));
  const shimmerMat = track(new MeshBasicMaterial({ color: colr(shade(PAL.gas, -0.1)), transparent: true, opacity: 0.035, blending: AdditiveBlending, depthWrite: false, fog: false, side: DoubleSide }));
  const amberMat = track(new MeshBasicMaterial({ color: colr(PAL.amber), fog: false, side: DoubleSide }));
  const brassMat = track(new MeshLambertMaterial({ color: colr(PAL.brass), flatShading: true }));
  patchMaterial(brassMat);

  // --- structure batch: floor, high dark ceiling (above the haze line), walls,
  //     nave funnel, coolant divider ------------------------------------------
  const st = new Batch();
  st.add(new BoxGeometry(82, 0.1, 50), MM(0, -0.05, -2));            // floor slab x[-41,41] z[-27,23]
  st.add(new BoxGeometry(82, 0.1, 50), MM(0, WALL_H + 1.2, -2));      // ceiling, high (dissolves into fog)
  st.add(new BoxGeometry(82, WALL_H, 0.2), MM(0, WALL_H / 2, -26));   // envelope N
  st.add(new BoxGeometry(82, 2.0, 0.2), MM(0, 1.0, 22));             // envelope S (low so top-down sees over)
  st.add(new BoxGeometry(0.2, WALL_H, 48), MM(-40, WALL_H / 2, -2)); // envelope W
  st.add(new BoxGeometry(0.2, WALL_H, 48), MM(40, WALL_H / 2, -2));  // envelope E
  // nave funnel (converging toward the Registrar's aperture, far -z) — M-E lands
  // the aperture ceremony; the funnel walls are built now so the sightline is coherent
  st.add(new BoxGeometry(0.3, WALL_H, 13.4).rotateY(Math.atan2(8, 12)), MM(-7, WALL_H / 2, -20));
  st.add(new BoxGeometry(0.3, WALL_H, 13.4).rotateY(-Math.atan2(8, 12)), MM(7, WALL_H / 2, -20));
  // coolant-gallery divider (3m opening at the landing edge)
  st.add(new BoxGeometry(0.2, WALL_H, 22), MM(-20, WALL_H / 2, 5));
  const stMesh = new Mesh(st.build(), structMat); stMesh.matrixAutoUpdate = false; group.add(stMesh);

  // --- racks: ONE InstancedMesh, matrices in aisle pairs (Hall A N-S, Hall B
  //     E-W). Fog eats the far half so density reads infinite. ---------------
  const rack = makeRackGeo();
  const mats: Matrix4[] = [];
  const ledRegions: number[] = []; // per-instance LED pattern index -> UV
  const ledMats: Matrix4[] = [];   // aisle-facing LED plane per rack
  const ledAtlas = makeLedAtlas();
  const pushRack = (x: number, z: number, rotY: number, faceDir: number, seed: number): void => {
    const m = new Matrix4().makeRotationY(rotY).setPosition(x + OX, 0, z + OZ);
    mats.push(m);
    const pat = Math.floor(h1(seed) * LED_PAT);
    ledRegions.push(pat);
    // LED plane on the aisle-facing long face (front recess at +local-Z, rotated)
    const lm = new Matrix4().makeRotationY(rotY + (faceDir < 0 ? Math.PI : 0));
    lm.setPosition(x + OX + (rotY === 0 ? 0 : faceDir * 0.32), 1.15, z + OZ + (rotY === 0 ? faceDir * 0.32 : 0));
    ledMats.push(lm);
  };
  // Hall A rows (N-S), racks placed along z; rot 0 (wide along x), face toward the flanking aisle
  const hallARows = [-15.5, -9.5, -3.5, 3.5, 9.5, 15.5];
  let seed = 1;
  for (const rx of hallARows) {
    const face = rx < 0 ? 1 : -1; // face the nearest aisle toward x=0 side
    for (let z = -5.4; z <= 13.4; z += 1.18) pushRack(rx, z, 0, face, seed++);
  }
  // Hall B rows (E-W), racks along x; rot 90
  const hallBRows = [-11.5, -8.5];
  for (const rz of hallBRows) {
    const face = rz < -10 ? 1 : -1;
    for (let x = -29; x <= 29; x += 1.42) pushRack(x, rz, Math.PI / 2, face, seed++);
  }
  const rackInst = new InstancedMesh(rack.geo, rackMat, mats.length);
  rackInst.instanceMatrix.setUsage(DynamicDrawUsage);
  for (let i = 0; i < mats.length; i++) rackInst.setMatrixAt(i, mats[i]);
  rackInst.instanceMatrix.needsUpdate = true;
  rackInst.frustumCulled = false;
  group.add(rackInst);

  // LED faces: ONE merged mesh (one draw) — a plane per rack whose UVs are baked
  // into its atlas sub-region so Hall A != Hall B from one texture. No custom
  // shader (robust): the atlas CanvasTexture redraws at 10Hz, so every face
  // animates its constellation live off its fixed region. fog:true so far faces
  // dissolve into the green-dark at the vanishing point.
  const led = new Batch();
  const facePlane = new PlaneGeometry(0.82, 1.9);
  for (let i = 0; i < ledMats.length; i++) led.add(facePlane.clone(), ledMats[i], ledAtlas.region(ledRegions[i]));
  facePlane.dispose();
  const ledMat = track(new MeshBasicMaterial({ map: ledAtlas.texture }));
  const ledMesh = new Mesh(led.build(), ledMat); ledMesh.matrixAutoUpdate = false;
  ledMesh.frustumCulled = false; group.add(ledMesh);

  // --- per-aisle underglow strips (the compositional spine) + additive cards -
  const under = new Batch();
  const glow = new Batch();
  const hallAAisles = [-12.5, -6.5, 0, 6.5, 12.5];
  for (const ax of hallAAisles) {
    under.add(new BoxGeometry(0.14, 0.05, 20), MM(ax, 0.03, 4));       // hard phosphor line at floor level
    glow.add(new PlaneGeometry(0.9, 20).rotateX(-Math.PI / 2), MM(ax, 0.14, 4));
  }
  for (const az of [-10]) { // Hall B hero aisle
    under.add(new BoxGeometry(60, 0.05, 0.14), MM(0, 0.03, az));
    glow.add(new PlaneGeometry(60, 0.9).rotateX(-Math.PI / 2), MM(0, 0.14, az));
  }
  const underMesh = new Mesh(under.build(), underMat); underMesh.matrixAutoUpdate = false; group.add(underMesh);
  const glowMesh = new Mesh(glow.build(), glowMat); glowMesh.matrixAutoUpdate = false; group.add(glowMesh);

  // --- coolant gallery: frost shells + additive cores + moving slugs ---------
  const cool = new Batch();
  const core = new Batch();
  const runXs = [-36, -33, -30, -27, -23];
  for (const cx of runXs) {
    cool.add(new BoxGeometry(0.4, 0.3, 21), MM(cx, 0.2, 5));
    core.add(new BoxGeometry(0.12, 0.12, 21), MM(cx, 0.2, 5));
  }
  const coolMesh = new Mesh(cool.build(), coolantShell); coolMesh.matrixAutoUpdate = false; group.add(coolMesh);
  const coreMesh = new Mesh(core.build(), coolantCore); coreMesh.matrixAutoUpdate = false; group.add(coreMesh);
  // slugs: your day's offerings arriving faster than you did (~0.4 m/s, moving -z/away)
  const slugGeo = new BoxGeometry(0.22, 0.22, 0.7);
  const slugInst = new InstancedMesh(slugGeo, slugMat, runXs.length);
  slugInst.instanceMatrix.setUsage(DynamicDrawUsage);
  const slugZ = runXs.map((_, i) => 15 - i * 4);
  const setSlug = (i: number): void => {
    slugInst.setMatrixAt(i, new Matrix4().setPosition(runXs[i] + OX, 0.2, slugZ[i] + OZ));
  };
  for (let i = 0; i < runXs.length; i++) setSlug(i);
  slugInst.instanceMatrix.needsUpdate = true;
  group.add(slugInst);

  // --- grated landing: crosshatch grate over an emissive phosphor under-plane -
  const [gc, gctx] = mkCanvas(128, 128);
  gctx.clearRect(0, 0, 128, 128);
  gctx.strokeStyle = css(shade(PAL.carbon, 0.1)); gctx.lineWidth = 6;
  for (let i = 0; i <= 128; i += 16) { gctx.beginPath(); gctx.moveTo(i, 0); gctx.lineTo(i, 128); gctx.stroke(); gctx.beginPath(); gctx.moveTo(0, i); gctx.lineTo(128, i); gctx.stroke(); }
  const grateTex = track(toTexture(gc, { nearest: true, repeat: true }));
  grateTex.repeat.set(9, 4);
  const grateMat = track(new MeshBasicMaterial({ map: grateTex, transparent: true, alphaTest: 0.3, side: DoubleSide }));
  const grate = new Mesh(new PlaneGeometry(18, 8).rotateX(-Math.PI / 2), grateMat);
  grate.position.set(0 + OX, 0.12, 18 + OZ); grate.matrixAutoUpdate = false; grate.updateMatrix(); group.add(grate);
  const underPlane = new Mesh(new PlaneGeometry(18, 8).rotateX(-Math.PI / 2), track(new MeshBasicMaterial({ color: colr(PAL.phosphor), transparent: true, opacity: 0.4, blending: AdditiveBlending, depthWrite: false, fog: false })));
  underPlane.position.set(0 + OX, 0.0, 18 + OZ); underPlane.matrixAutoUpdate = false; underPlane.updateMatrix(); group.add(underPlane);

  // --- ground haze: the first phosphor breath, pooling green at the ankles ---
  const haze = new Mesh(new PlaneGeometry(60, 34).rotateX(-Math.PI / 2), hazeMat);
  haze.position.set(-6 + OX, 0.3, 8 + OZ); haze.matrixAutoUpdate = false; haze.updateMatrix(); group.add(haze);

  // --- heat shimmer: tall low-opacity additive planes off the hottest aisles -
  const shimGeo = new PlaneGeometry(1.2, 3.4);
  const shimInst = new InstancedMesh(shimGeo, shimmerMat, hallAAisles.length * 2);
  let si = 0;
  for (const ax of hallAAisles) for (const z of [-3, -9]) {
    shimInst.setMatrixAt(si++, new Matrix4().setPosition(ax + OX, 1.8, z + OZ));
  }
  shimInst.instanceMatrix.needsUpdate = true;
  shimInst.frustumCulled = false; group.add(shimInst);

  // --- gauge manifolds (2-V reskin) at gallery N & S ends + valve wheels ------
  const brass = new Batch();
  for (const [gx, gz] of [[-30, -4], [-30, 14]] as [number, number][]) {
    brass.add(new BoxGeometry(1.4, 1.2, 0.3), MM(gx, 1.2, gz));       // manifold box
    brass.add(new BoxGeometry(0.5, 0.5, 0.1), MM(gx - 0.35, 1.4, gz + 0.2)); // dial
    brass.add(new BoxGeometry(0.5, 0.5, 0.1), MM(gx + 0.35, 1.4, gz + 0.2)); // dial
    brass.add(new BoxGeometry(0.1, 0.5, 0.1), MM(gx - 0.35, 0.6, gz + 0.2)); // wheel spoke
  }
  // aisle-end terminals (4-T reprise) at Hall A north ends
  for (const tx of [0]) {
    brass.add(new BoxGeometry(0.7, 1.3, 0.5), MM(tx, 0.65, -6));
    brass.add(new BoxGeometry(0.6, 0.4, 0.05), MM(tx, 1.1, -5.7));
  }
  // intake hatch block at the nave throat (6-F home)
  brass.add(new BoxGeometry(1.6, 1.4, 0.6), MM(0, 0.7, -14));
  brass.add(new BoxGeometry(1.2, 0.12, 0.4), MM(0, 1.2, -13.7));
  // valve at the coolant run (6-K home)
  brass.add(new BoxGeometry(0.3, 0.3, 0.3), MM(-30, 0.4, 6));
  const brassMesh = new Mesh(brass.build(), brassMat); brassMesh.matrixAutoUpdate = false; group.add(brassMesh);

  // amber pilot lamps: one KEPT DEFECT in the eighth rack (arrival-frame amber)
  const amberPilots = new Batch();
  amberPilots.add(new PlaneGeometry(0.09, 0.09), MM(3.5, 1.7, -5.3));
  const amberMesh = new Mesh(amberPilots.build(), amberMat); amberMesh.matrixAutoUpdate = false; group.add(amberMesh);

  // --- signage: rack-end plates (verbatim) + phosphor wayfinding decals ------
  const signLines = [mf.rackHallSignage[0], mf.rackHallSignage[4], mf.rackHallSignage[9], mf.rackHallSignage[12]];
  const sAtlas = makeSignAtlas(signLines);
  const signMat = track(new MeshBasicMaterial({ map: track(sAtlas.texture), transparent: true, side: DoubleSide }));
  const signs = new Batch();
  const signSpots: [number, number, number, number, number][] = [
    [-15.5, 2.0, 14, Math.PI, 0], [15.5, 2.0, 14, Math.PI, 1], [-30, 2.2, -6, Math.PI, 2], [9.5, 2.0, 14, Math.PI, 3],
  ];
  for (const [x, y, z, yaw, i] of signSpots) {
    signs.add(new PlaneGeometry(1.7, 0.7), new Matrix4().makeRotationY(yaw).premultiply(MM(x, y, z)), sAtlas.region(i));
  }
  const signMesh = new Mesh(signs.build(), signMat); signMesh.matrixAutoUpdate = false; group.add(signMesh);

  // wayfinding decals (phosphor arrows + DO-NOT-STAND + yellow line), on the floor
  const decalMat = track(new MeshBasicMaterial({ map: track(makeDecal(mf.wayfinding[0])), transparent: true, depthWrite: false, fog: false }));
  const arrow = new Mesh(new PlaneGeometry(2.2, 0.55).rotateX(-Math.PI / 2), decalMat);
  arrow.position.set(0 + OX, 0.13, 12 + OZ); arrow.matrixAutoUpdate = false; arrow.updateMatrix(); group.add(arrow);
  const dnsMat = track(new MeshBasicMaterial({ map: track(makeDecal(mf.wayfinding[1])), transparent: true, depthWrite: false, fog: false }));
  const dns = new Mesh(new PlaneGeometry(3.0, 0.5).rotateX(-Math.PI / 2), dnsMat);
  dns.position.set(0 + OX, 0.13, 15 + OZ); dns.matrixAutoUpdate = false; dns.updateMatrix(); group.add(dns);

  // --- chyron cornice (the Machine's voice travels with you) -----------------
  const chyron = new Chyron(reducedMotion);
  const chyronMat = track(new MeshBasicMaterial({ map: chyron.texture, transparent: true }));
  patchMaterial(chyronMat, { dimExempt: true });
  const cornice = new Mesh(new PlaneGeometry(7, 0.9), chyronMat);
  cornice.position.set(0 + OX, 3.4, 21.8 + OZ); cornice.rotation.y = Math.PI;
  cornice.matrixAutoUpdate = false; cornice.updateMatrix(); group.add(cornice);

  // --- collision (LOCAL coords; the grid is offset-applied by the manager via
  //     manifest.bounds/world). Aisles are the NEGATIVE SPACE. ----------------
  const wl = (x0: number, z0: number, x1: number, z1: number, hh = WALL_H): WallSeg => ({ x0: x0 + OX, z0: z0 + OZ, x1: x1 + OX, z1: z1 + OZ, h: hh });
  const sr = (x0: number, z0: number, x1: number, z1: number): SolidRect => ({ x0: x0 + OX, z0: z0 + OZ, x1: x1 + OX, z1: z1 + OZ });
  const walls: WallSeg[] = [
    wl(-40, -26, 40, -26), wl(-40, 22, 40, 22, 2.0), wl(-40, -26, -40, 22), wl(40, -26, 40, 22),
    wl(-11, -14, -3, -26), wl(11, -14, 3, -26), wl(-20, -6, -20, 16),
  ];
  const solids: SolidRect[] = [];
  for (const rx of hallARows) solids.push(sr(rx - 0.5, -6, rx + 0.5, 14));      // 6 Hall A rows
  for (const rz of hallBRows) solids.push(sr(-30, rz - 0.5, 30, rz + 0.5));     // 2 Hall B rows
  // THE COOLANT GALLERY (audit B1): one slim collider per glass run — NOT a
  // single solid block. The aisles BETWEEN the runs are the walkable negative
  // space, so the player walks in and the 6-K valve + 2-V manifold hotspots
  // (dead-center on the x=-30 run) arm from an adjacent aisle ~0.7m away. The
  // footprint is derived from `runXs` and matches the rendered frost runs
  // exactly (0.4m wide, z[-5.5,15.5]) so collision never drifts from the glass.
  for (const cx of runXs) solids.push(sr(cx - 0.2, -5.5, cx + 0.2, 15.5));      // 5 coolant glass runs
  solids.push(sr(-31, -5, -29, -3));   // gauge manifold N
  solids.push(sr(-31, 13, -29, 15));   // gauge manifold S
  solids.push(sr(-1, -14.6, 1, -13.4)); // intake-hatch block
  solids.push(sr(6.6, 19, 9.4, 21));   // cab surround (SE)
  const zones: ZoneRect[] = [];
  const bounds: CollisionBounds = { x0: -41 + OX, z0: -27 + OZ, x1: 41 + OX, z1: 23 + OZ };

  const spawns: Record<string, ZoneSpawn> = {
    arrival: { x: 0 + OX, z: 20 + OZ, facing: Math.PI },   // conscription stair (deferred entry)
    from_cab: { x: 7 + OX, z: 19 + OZ, facing: Math.PI },  // elevator arrival, SE corner
    resume: { x: 0 + OX, z: 20 + OZ, facing: Math.PI },    // mid-conscription reload
  };

  const manifest: ZoneManifest = {
    id: 'machine_floor', name: 'THE MACHINE FLOOR', residency: 'streamed', slabY: -6,
    bounds, spawns,
    connections: [
      { toZone: 'floor7', kind: 'stair', triggerId: 'ascent_stair', masked: true, overrideable: false },
      { toZone: 'vertical_core', kind: 'elevator', triggerId: 'cab_up', masked: true, overrideable: true },
    ],
    collision: { walls, solids, zones },
    coexistWithResident: false,
  };

  // hotspots — world coords (offset applied). system ids map to maintenance runners
  // in the controller. `enabled` gates the duty-locked ascent stair.
  const H = (id: string, lx: number, lz: number, reach: number, prompt: string, enabled?: () => boolean): ZoneHotspot =>
    ({ id, x: lx + OX, z: lz + OZ, reach, prompt, enabled });
  const hotspots: ZoneHotspot[] = [
    H('cab_up', 8, 20, 1.6, 'THE CAB — E'),
    H('coolant_valve', -30, 6, 1.6, 'COOLANT RUN — E'),          // 6-K (playable)
    H('rack_rite_a', 9.5, -2, 1.5, 'RACK FACE — E'),             // 6-P (playable)
    H('coolant_gauge_n', -30, -4, 1.7, 'MANIFOLD (NORTH) — E'),  // 2-V machine variant (stub)
    H('terminal_a', 0, -6, 1.7, 'AISLE-END TERMINAL — E'),       // 4-T reprise (stub)
    H('intake_hatch', 0, -14, 1.9, 'THE INTAKE HATCH — E'),      // 6-F (stub)
    H('drawer_reseat_a', 3.5, 2, 1.5, 'AISLE A — E'),            // 6-R (stub)
  ];

  // B1 REACHABILITY TRIPWIRE (dev-only; `__DEV__` is defined false + dead-code-
  // stripped in production). Every maintenance station must have standable floor
  // within its reach — the regression guard for the coolant-gallery "one solid
  // block" bug that buried the 6-K valve. Samples a disc around each hotspot for
  // one capsule-clear (RADIUS 0.35) cell, using the same 9-point capsule pattern
  // as collision.ts::circleBlocked. The stations sit clear of walls, so the
  // local `solids` are the only obstacles that matter here.
  if (__DEV__) {
    const R = 0.35, dd = R * 0.7071;
    const inSolid = (x: number, z: number): boolean =>
      solids.some((s) => x >= s.x0 && x <= s.x1 && z >= s.z0 && z <= s.z1);
    const capsuleClear = (x: number, z: number): boolean =>
      !inSolid(x, z) && !inSolid(x + R, z) && !inSolid(x - R, z) && !inSolid(x, z + R) && !inSolid(x, z - R) &&
      !inSolid(x + dd, z + dd) && !inSolid(x - dd, z + dd) && !inSolid(x + dd, z - dd) && !inSolid(x - dd, z - dd);
    for (const h of hotspots) {
      if (h.id === 'cab_up') continue; // the landing threshold, not a maintenance station
      let reachable = false;
      for (let a = 0; a < 24 && !reachable; a++) {
        const ang = (a / 24) * Math.PI * 2;
        for (let rr = 0.4; rr <= h.reach && !reachable; rr += 0.2) {
          const px = h.x + Math.cos(ang) * rr, pz = h.z + Math.sin(ang) * rr;
          if (px >= bounds.x0 && px <= bounds.x1 && pz >= bounds.z0 && pz <= bounds.z1 && capsuleClear(px, pz)) reachable = true;
        }
      }
      if (!reachable) console.warn(`[machine-floor] hotspot '${h.id}' has no standable floor within reach ${h.reach} — B1-class regression`);
    }
  }

  const dispose = (): void => {
    group.traverse((o) => { const m = o as Mesh; if (m.geometry) m.geometry.dispose(); });
    rack.geo.dispose(); slugGeo.dispose(); shimGeo.dispose();
    ledAtlas.texture.dispose();
    for (const d of disposables) d.dispose();
    chyron.texture.dispose();
    group.clear();
  };

  // --- animation ticks -------------------------------------------------------
  let ledT = 0, ledBucket = 0;
  const tickLED = (dt: number): void => {
    ledT += dt;
    if (ledT >= 0.1) { ledT = 0; ledBucket++; ledAtlas.redraw(ledBucket, !reducedMotion); }
  };
  let pulse = 0, pulseTarget = 0; // diegetic verdict: underglow brighten/dim one step
  const baseUnder = colr(PAL.phosphor);
  const tickCoolant = (dt: number): void => {
    if (!reducedMotion) {
      for (let i = 0; i < runXs.length; i++) {
        slugZ[i] -= dt * 0.4; // arriving faster than you did, moving away (-z)
        if (slugZ[i] < -8) slugZ[i] = 16;
        setSlug(i);
      }
      slugInst.instanceMatrix.needsUpdate = true;
    }
    // ease the verdict pulse back to neutral
    if (pulse !== pulseTarget) {
      pulse += Math.sign(pulseTarget - pulse) * Math.min(Math.abs(pulseTarget - pulse), dt * 0.9);
      const k = 1 + pulse * 0.35; // +/- brighten a step
      underMat.color.copy(baseUnder).multiplyScalar(Math.max(0.35, k));
      if (Math.abs(pulse - pulseTarget) < 0.01 && pulseTarget !== 0) pulseTarget = 0; // decay back
    }
  };
  let shimT = 0;
  const tickShimmer = (dt: number): void => {
    if (reducedMotion) return;
    shimT += dt;
    shimmerMat.opacity = 0.03 + 0.012 * Math.sin(shimT * 1.7);
  };
  const respondVerdict = (band: 'APPROVED' | 'AWD' | 'TOLERATED' | 'DENIED'): void => {
    // the aisle RESPONDS (no chit): APPROVED brightens a step, DENIED dims a step,
    // TOLERATED nothing (the ordinariness is the reading), always recoverable.
    pulse = 0;
    pulseTarget = band === 'APPROVED' ? 1 : band === 'AWD' ? 0.4 : band === 'DENIED' ? -1 : 0;
  };

  const tris = st.tris + rack.tris * mats.length + led.tris + under.tris + glow.tris +
    cool.tris + core.tris + runXs.length * 12 + brass.tris + signs.tris + amberPilots.tris +
    shimInst.count * 2 + 40;

  return {
    group, manifest, hotspots, chyron, tickLED, tickCoolant, tickShimmer, respondVerdict,
    tris, buildMs: performance.now() - t0, dispose,
  };
}

// build-time flag (esbuild `define`): true in dev, false + dead-code-stripped in
// production — gates the B1 reachability tripwire above.
declare const __DEV__: boolean;
